import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Paper,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { collection, addDoc, serverTimestamp, query, getDocs, where, doc, getDoc } from 'firebase/firestore';
import { COLLECTIONS } from '../../firebase/collections';
import { NotificationService } from '../../services/NotificationService';

/**
 * Composant pour créer une nouvelle demande de travail
 */
const NewWorkRequestForm = ({ onRequestCreated }) => {
  // Récupérer les données du brouillon depuis le localStorage au chargement
  const [formData, setFormData] = useState(() => {
    const savedDraft = localStorage.getItem('workRequestDraft');
    return savedDraft ? JSON.parse(savedDraft) : {
      title: '',
      description: '',
      category: '',
      customCategory: '',
      priority: 'medium',
      location: '',
      contactInfo: ''
    };
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(() => {
    const savedDraft = localStorage.getItem('workRequestDraft');
    return savedDraft ? JSON.parse(savedDraft).selectedService || '' : '';
  });
  const [draftSaved, setDraftSaved] = useState(false);
  const [autosaveTimer, setAutosaveTimer] = useState(null);
  const { currentUser, userProfile, userSchool, userService } = useAuth();

  // Récupérer la liste des services techniques disponibles
  useEffect(() => {
    const fetchServices = async () => {
      try {
        console.log('Début de la récupération des services techniques...');
        console.log('userProfile:', userProfile);
        
        // D'abord, essayons de récupérer tous les services sans filtre pour déboguer
        const allServicesQuery = query(collection(db, COLLECTIONS.SERVICES));
        const allServicesSnapshot = await getDocs(allServicesQuery);
        
        console.log('Tous les services dans Firestore:', allServicesSnapshot.size);
        allServicesSnapshot.docs.forEach(doc => {
          console.log('Service trouvé:', doc.id, doc.data());
        });
        
        // Essayons plusieurs approches pour récupérer les services techniques
        let servicesData = [];
        
        // 1. Récupérer le service spécifique vu dans la capture d'écran
        const specificServiceId = "XYAQgc4lCAUp1a8mgWoeG2f7CR12";
        try {
          const serviceDocRef = doc(db, COLLECTIONS.SERVICES, specificServiceId);
          const serviceDocSnap = await getDoc(serviceDocRef);
          
          if (serviceDocSnap.exists()) {
            const data = serviceDocSnap.data();
            console.log('Service spécifique trouvé:', data);
            servicesData.push({
              id: specificServiceId,
              name: data.displayName || "SG",
              ...data
            });
          } else {
            console.log('Service spécifique non trouvé avec l\'ID:', specificServiceId);
          }
        } catch (specificError) {
          console.error('Erreur lors de la récupération du service spécifique:', specificError);
        }
        
        // 2. Si le service spécifique n'est pas trouvé, essayer avec role === 'technical'
        if (servicesData.length === 0) {
          const technicalQuery = query(
            collection(db, COLLECTIONS.SERVICES),
            where('role', '==', 'technical')
          );
          
          const technicalSnapshot = await getDocs(technicalQuery);
          console.log('Services avec role=technical:', technicalSnapshot.size);
          
          if (technicalSnapshot.size > 0) {
            technicalSnapshot.docs.forEach(doc => {
              const data = doc.data();
              servicesData.push({
                id: doc.id,
                name: data.displayName || data.name || 'Service sans nom',
                ...data
              });
            });
          }
        }
        
        // 3. Si toujours rien, essayer avec la collection USERS
        if (servicesData.length === 0) {
          const usersQuery = query(
            collection(db, COLLECTIONS.USERS),
            where('role', '==', 'technical')
          );
          
          const usersSnapshot = await getDocs(usersQuery);
          console.log('Utilisateurs avec role=technical:', usersSnapshot.size);
          
          usersSnapshot.docs.forEach(doc => {
            const data = doc.data();
            servicesData.push({
              id: doc.id,
              name: data.displayName || data.name || 'Service sans nom',
              ...data
            });
          });
        }
        
        // 4. En dernier recours, ajouter manuellement le service vu dans la capture d'écran
        if (servicesData.length === 0) {
          console.log('Aucun service trouvé, ajout manuel du service vu dans la capture');
          servicesData.push({
            id: "XYAQgc4lCAUp1a8mgWoeG2f7CR12",
            name: "SG",
            role: "technical",
            active: true
          });
        }
        
        console.log('Services techniques finaux:', servicesData);
        setServices(servicesData);
      } catch (error) {
        console.error('Erreur lors de la récupération des services techniques:', error);
        // En cas d'erreur, ajouter manuellement le service vu dans la capture
        setServices([{
          id: "XYAQgc4lCAUp1a8mgWoeG2f7CR12",
          name: "SG",
          role: "technical",
          active: true
        }]);
      }
    };

    fetchServices();
  }, []);

  // Fonction pour sauvegarder le brouillon dans le localStorage
  const saveDraft = useCallback(() => {
    const draftData = {
      ...formData,
      selectedService
    };
    localStorage.setItem('workRequestDraft', JSON.stringify(draftData));
    setDraftSaved(true);
    
    // Masquer le message de sauvegarde après 3 secondes
    setTimeout(() => {
      setDraftSaved(false);
    }, 3000);
  }, [formData, selectedService]);

  // Sauvegarde automatique lorsque le formulaire change
  useEffect(() => {
    // Annuler le timer précédent s'il existe
    if (autosaveTimer) {
      clearTimeout(autosaveTimer);
    }
    
    // Définir un nouveau timer pour la sauvegarde automatique (2 secondes après la dernière modification)
    const timer = setTimeout(() => {
      saveDraft();
    }, 2000);
    
    setAutosaveTimer(timer);
    
    // Nettoyer le timer lors du démontage du composant
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [formData, selectedService, saveDraft]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Effacer l'erreur pour ce champ si elle existe
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleServiceChange = (e) => {
    const serviceId = e.target.value;
    console.log('Service sélectionné:', serviceId);
    setSelectedService(serviceId);
    
    // Effacer l'erreur pour ce champ si elle existe
    if (errors.service) {
      setErrors(prev => ({
        ...prev,
        service: ''
      }));
    }
  };
  
  // Fonction pour effacer le brouillon
  const clearDraft = () => {
    localStorage.removeItem('workRequestDraft');
    setFormData({
      title: '',
      description: '',
      category: '',
      customCategory: '',
      priority: 'medium',
      location: '',
      contactInfo: ''
    });
    setSelectedService('');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }
    
    if (!formData.category.trim()) {
      newErrors.category = 'La catégorie est requise';
    }
    
    // Valider la catégorie personnalisée si "Autre" est sélectionné
    if (formData.category === 'Autre' && !formData.customCategory.trim()) {
      newErrors.customCategory = 'Veuillez préciser la catégorie';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Le lieu est requis';
    }
    
    // Vérifier si des services techniques sont disponibles
    if (services.length === 0) {
      newErrors.service = 'Aucun service technique disponible. Impossible de soumettre la demande.';
    } else if (!selectedService) {
      newErrors.service = 'Veuillez sélectionner un service technique';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Créer la nouvelle demande
      const newWorkRequest = {
        title: formData.title,
        description: formData.description,
        category: formData.category === 'Autre' ? formData.customCategory : formData.category,
        priority: formData.priority,
        status: 'pending',
        location: formData.location,
        contactInfo: formData.contactInfo || '',
        createdBy: currentUser.uid,
        createdByName: userProfile?.displayName || currentUser.email,
        requestingServiceId: userService?.id || currentUser.uid,
        requestingServiceName: userService?.name || userProfile?.displayName || currentUser.email || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        technicalServiceId: selectedService,
        assignedServiceId: selectedService,
        schoolId: userSchool?.id || null,
        comments: []
      };
      
      // Ajouter le service sélectionné si disponible
      if (selectedService) {
        const selectedServiceData = services.find(s => s.id === selectedService);
        if (selectedServiceData) {
          newWorkRequest.assignedServiceName = selectedServiceData.name;
        }
      }
      
      // Enregistrer dans Firestore
      const docRef = await addDoc(collection(db, COLLECTIONS.WORK_REQUESTS), newWorkRequest);
      
      // Créer une notification pour le service technique
      if (selectedService) {
        const selectedServiceData = services.find(s => s.id === selectedService);
        if (selectedServiceData) {
          console.log('NewWorkRequestForm: Création de notification pour le service', selectedServiceData);
          await NotificationService.createWorkRequestNotification(
            { ...newWorkRequest, id: docRef.id },
            selectedServiceData
          );
        }
      }
      
      // Réinitialiser le formulaire et supprimer le brouillon
      clearDraft();
      
      // Afficher le message de succès
      setSuccess(true);
      
      // Notifier le parent que la demande a été créée
      if (onRequestCreated) {
        onRequestCreated({ id: docRef.id, ...newWorkRequest });
      }
    } catch (error) {
      console.error('Erreur lors de la création de la demande:', error);
      setErrors(prev => ({
        ...prev,
        submit: 'Une erreur est survenue lors de la création de la demande.'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Créer une nouvelle demande
        </Typography>
        {draftSaved && (
          <Typography variant="body2" color="success.main">
            Brouillon sauvegardé automatiquement
          </Typography>
        )}
      </Box>
      
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              name="title"
              label="Titre de la demande"
              fullWidth
              required
              value={formData.title}
              onChange={handleChange}
              error={!!errors.title}
              helperText={errors.title}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="description"
              label="Description détaillée"
              fullWidth
              required
              multiline
              rows={4}
              value={formData.description}
              onChange={handleChange}
              error={!!errors.description}
              helperText={errors.description}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!errors.category}>
              <InputLabel>Catégorie</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={(e) => {
                  handleChange(e);
                  // Réinitialiser la catégorie personnalisée si on change de catégorie
                  if (e.target.value !== 'Autre') {
                    setFormData(prev => ({ ...prev, customCategory: '' }));
                    setErrors(prev => ({ ...prev, customCategory: '' }));
                  }
                }}
                label="Catégorie"
                disabled={loading}
              >
                <MenuItem value="Plomberie">Plomberie</MenuItem>
                <MenuItem value="Électricité">Électricité</MenuItem>
                <MenuItem value="Menuiserie">Menuiserie</MenuItem>
                <MenuItem value="Peinture">Peinture</MenuItem>
                <MenuItem value="Climatisation">Climatisation</MenuItem>
                <MenuItem value="Informatique">Informatique</MenuItem>
                <MenuItem value="Nettoyage">Nettoyage</MenuItem>
                <MenuItem value="Jardinage">Jardinage</MenuItem>
                <MenuItem value="Serrurerie">Serrurerie</MenuItem>
                <MenuItem value="Autre">Autre</MenuItem>
              </Select>
              {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
            </FormControl>
          </Grid>
          
          {/* Champ personnalisé pour "Autre" */}
          {formData.category === 'Autre' && (
            <Grid item xs={12} sm={6}>
              <TextField
                name="customCategory"
                label="Précisez la catégorie"
                fullWidth
                required
                value={formData.customCategory}
                onChange={handleChange}
                error={!!errors.customCategory}
                helperText={errors.customCategory || "Ex: Reprographie, Restauration, etc."}
                disabled={loading}
                placeholder="Entrez la catégorie personnalisée"
              />
            </Grid>
          )}
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!errors.priority}>
              <InputLabel>Priorité</InputLabel>
              <Select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                disabled={loading}
              >
                <MenuItem value="low">Basse</MenuItem>
                <MenuItem value="medium">Moyenne</MenuItem>
                <MenuItem value="high">Haute</MenuItem>
                <MenuItem value="urgent">Urgente</MenuItem>
              </Select>
              {errors.priority && <FormHelperText>{errors.priority}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="location"
              label="Lieu / Emplacement"
              fullWidth
              required
              value={formData.location}
              onChange={handleChange}
              error={!!errors.location}
              helperText={errors.location}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="contactInfo"
              label="Informations de contact supplémentaires"
              fullWidth
              value={formData.contactInfo}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControl fullWidth required error={!!errors.service || services.length === 0}>
              <InputLabel id="service-label">Service technique</InputLabel>
              <Select
                labelId="service-label"
                id="service-select"
                name="service"
                value={selectedService}
                onChange={handleServiceChange}
                label="Service technique"
                disabled={loading}
                displayEmpty
                sx={{ '& .MuiSelect-select': { whiteSpace: 'normal' } }}
              >
                <MenuItem value="" disabled>
                  <Typography noWrap>Sélectionner un service technique</Typography>
                </MenuItem>
                {services && services.length > 0 ? (
                  services.map((service) => (
                    <MenuItem key={service.id} value={service.id}>
                      <Typography noWrap>{service.name || 'Service sans nom'}</Typography>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="no-service" disabled>
                    <Typography noWrap>Aucun service technique disponible</Typography>
                  </MenuItem>
                )}
              </Select>
              {errors.service ? (
                <FormHelperText>{errors.service}</FormHelperText>
              ) : services.length === 0 ? (
                <FormHelperText error>
                  Aucun service technique n'est disponible. Veuillez contacter l'administrateur.
                </FormHelperText>
              ) : null}
            </FormControl>
          </Grid>
          
          {errors.submit && (
            <Grid item xs={12}>
              <Alert severity="error">{errors.submit}</Alert>
            </Grid>
          )}
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={clearDraft}
                disabled={loading}
              >
                Effacer le brouillon
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={saveDraft}
                  disabled={loading}
                >
                  Sauvegarder
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  sx={{ minWidth: 150 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Soumettre la demande'}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Demande créée avec succès !
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default NewWorkRequestForm;
